import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React, { useCallback, useState } from 'react';
import { WorkflowDto } from 'src/api/FiretrolApiClient';
import { useAuth } from 'src/hooks/useAuth';
import { logger } from 'src/utils';
import { PendingReviewUI } from './PendingReviewUI';
import { useWorkflowActionButtons } from './useWorkflowActionButtons';
import { useWorkflowState } from './useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';

export interface IWorkflowButtonsProps {
  error?: string | null;
  stepIndex: number;
  onSubmitClick: (nextStatus: string, nextStepIndex: number) => void;
  onSaveOnlyClick: () => void;
  onVoidClick: () => void;
  workflow: WorkflowDto | null;
  disabled?: boolean;
  onResendEmailClick?: () => Promise<void>;
  isAdmin?: boolean;
  isOwner?: boolean;
}

/** @registry Renders the status-transition button bar (draft/submit/reject/void/update) for a job workflow, gated by role/user authorization. */
export const WorkflowButtons: React.FC<IWorkflowButtonsProps> = ({
  error,
  onVoidClick,
  workflow,
  stepIndex,
  onSubmitClick,
  onSaveOnlyClick,
  disabled = false,
  onResendEmailClick,
  isAdmin: isAdminOverride,
  isOwner = false,
}) => {
  const { isAdmin } = useAuth();
  const isAdminEffective = isAdminOverride ?? isAdmin;

  const [loading, setLoading] = useState('');

  const {
    currentStep,
    nextStep,
    isCreating,
    isNextStepBatchHandled,
    authProps,
  } = useWorkflowState(workflow, stepIndex);

  const doSubmit = useCallback(
    async (nextStatus: string, nextId: number) => {
      setLoading(nextStatus);
      try {
        await onSubmitClick(nextStatus, nextId);
      } catch (err) {
        logger.error('Submit failed', { err });
      }
      setLoading('');
    },
    [onSubmitClick],
  );

  const doSaveOnly = useCallback(
    async (nextStatus: string) => {
      setLoading(nextStatus);
      try {
        if (nextStatus === 'Voided') {
          await onVoidClick();
        } else {
          await onSaveOnlyClick();
        }
      } catch (err) {
        logger.error('Save failed', { err });
      }
      setLoading('');
    },
    [onVoidClick, onSaveOnlyClick],
  );

  const isPendingReview =
    currentStep?.isEmployeeReviewStep || currentStep?.isSupervisorReviewStep;
  const pendingReviewTitle = currentStep?.isEmployeeReviewStep
    ? 'Pending Employee Review'
    : currentStep?.isSupervisorReviewStep
    ? 'Pending Supervisor Review'
    : null;

  const isEditable = isStepEditable({
    isAdmin: isAdminEffective,
    currentStep,
    status: undefined,
  });

  const {
    DraftButton,
    SubmitButton,
    VoidButton,
    RejectButton,
    ReviseButton,
    UpdateButton,
    BatchHandledMessage,
  } = useWorkflowActionButtons({
    currentStep,
    nextStep,
    isCreating,
    isNextStepBatchHandled,
    authProps,
    workflow: workflow ?? { allowDraft: false },
    isAdminEffective,
    isOwner,
    disabled,
    loading,
    isEditable,
    doSubmit,
    doSaveOnly,
    stepIndex,
  });

  if (!workflow) {
    return (
      <Paper>
        <Text>Something went wrong loading the workflow.</Text>
      </Paper>
    );
  }

  // Non-admins see only the review-pending UI with no action buttons.
  if (isPendingReview && !isAdminEffective) {
    return (
      <PendingReviewUI
        title={pendingReviewTitle!}
        isLoading={loading === 'ResendEmail'}
        onResendEmailClick={onResendEmailClick}
        onLoadingChange={(isLoading) =>
          setLoading(isLoading ? 'ResendEmail' : '')
        }
      />
    );
  }

  const hasContent =
    UpdateButton ||
    DraftButton ||
    SubmitButton ||
    RejectButton ||
    ReviseButton ||
    VoidButton ||
    BatchHandledMessage ||
    error ||
    currentStep?.isExternalStep ||
    (isPendingReview && isAdminEffective);

  if (!hasContent) return null;

  return (
    <Paper>
      <Stack>
        {isPendingReview && isAdminEffective && pendingReviewTitle && (
          <PendingReviewUI
            title={pendingReviewTitle}
            isLoading={loading === 'ResendEmail'}
            onResendEmailClick={onResendEmailClick}
            onLoadingChange={(isLoading) =>
              setLoading(isLoading ? 'ResendEmail' : '')
            }
          />
        )}

        {error && (
          <Alert title="Error" color="red">
            {error}
          </Alert>
        )}

        <Group gap="md" align="center">
          {UpdateButton}
          {DraftButton}
          {RejectButton}
          {ReviseButton}
          {VoidButton}
          {SubmitButton}
          {BatchHandledMessage}
        </Group>
      </Stack>
    </Paper>
  );
};
